// App.tsx
import "./App.css";
import Map from "./Component/Map";
import { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import Dashboard from "./Component/Dashboard/index";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_USER_REGISTRY_BASE_URL;

// Keys for localStorage
const ACCESS_TOKEN_KEY = "access_token";

// Axios instance for authenticated requests
export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "X-FROM-ASSET-REGISTRY": "True" },
});

// Axios instance for refresh (no access token attached)
const refreshApi = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "X-FROM-ASSET-REGISTRY": "True" },
});

// Attach access token to requests (except /refresh)
api.interceptors.request.use((config) => {
  if (config.url?.endsWith("/refresh")) return config;

  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    if (config.headers instanceof axios.AxiosHeaders) {
      config.headers.set("Authorization", `Bearer ${token}`);
    } else {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }
  return config;
});

// Handle 401: refresh access token using refresh cookie
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const res = await refreshApi.get("/refresh");
        const { access_token } = res.data;
        if (!access_token) throw new Error("Failed to refresh access token");

        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);

        if (originalRequest.headers instanceof axios.AxiosHeaders) {
          originalRequest.headers.set("Authorization", `Bearer ${access_token}`);
        } else {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${access_token}`,
          };
        }

        return axios(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed:", refreshError);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

function App() {
  useEffect(() => {
    const initializeTokens = async () => {
      try {
        const res = await refreshApi.get("/refresh");
        const { access_token } = res.data;
        if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);

        console.info("Tokens initialized:", { access_token });
      } catch (err) {
        console.warn("Initial token refresh failed:", err);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }
    };

    initializeTokens();
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="App">
            <Map />
          </div>
        }
      />
      <Route path="/dashboard" element={<Dashboard />} />
    </Routes>
  );
}

export default App;
